import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '../lib/supabaseClient';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';

function CustomsModal({ isOpen, onClose, customs, onSuccess }) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Map related states
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [viewport, setViewport] = useState({
    center: { lat: 41.0082, lng: 28.9784 }, // Default to Istanbul
    zoom: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (customs) {
      setValue('customs_code', customs.customs_code);
      setValue('customs_name', customs.customs_name);
      setValue('city', customs.city);
      setValue('country', customs.country);
      setValue('latitude', customs.latitude || '');
      setValue('longitude', customs.longitude || '');

      // If customs has coordinates, set the map location
      if (customs.latitude && customs.longitude) {
        const lat = parseFloat(customs.latitude);
        const lng = parseFloat(customs.longitude);
        setSelectedLocation({ lat, lng });
        setViewport({
          center: { lat, lng },
          zoom: 14,
        });
        setIsMapVisible(true);
      }
    } else {
      reset({
        customs_code: '',
        customs_name: '',
        city: '',
        country: '',
        latitude: '',
        longitude: '',
      });
    }
  }, [customs, setValue, reset]);

  // Handle map location selection
  const handleMapClick = (event) => {
    const lat = event.detail.latLng.lat;
    const lng = event.detail.latLng.lng;

    setSelectedLocation({ lat, lng });
    setViewport({
      ...viewport,
      center: { lat, lng },
    });

    // Update form values
    setValue('latitude', lat.toString());
    setValue('longitude', lng.toString());

    // Reverse geocode to get address details
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const addressComponents = results[0].address_components;

          // Update city and country fields
          const city =
            addressComponents.find((c) => c.types.includes('administrative_area_level_1'))
              ?.long_name || '';
          const country =
            addressComponents.find((c) => c.types.includes('country'))
              ?.short_name || '';

          setValue('city', city);
          setValue('country', country);

          // Update search query with formatted address
          setSearchQuery(results[0].formatted_address);
        }
      });
    }
  };

  // Toggle map visibility
  const toggleMap = () => {
    setIsMapVisible(!isMapVisible);
  };

  // Find location from coordinates
  const findLocationFromCoordinates = () => {
    const latitude = parseFloat(
      document.querySelector('input[name="latitude"]').value
    );
    const longitude = parseFloat(
      document.querySelector('input[name="longitude"]').value
    );

    if (!isNaN(latitude) && !isNaN(longitude)) {
      setSelectedLocation({ lat: latitude, lng: longitude });
      setViewport({
        center: { lat: latitude, lng: longitude },
        zoom: 14,
      });
      setIsMapVisible(true);

      // Reverse geocode to get address details
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            if (status === 'OK' && results[0]) {
              const addressComponents = results[0].address_components;

              // Update city and country fields
              const city =
                addressComponents.find((c) => c.types.includes('administrative_area_level_1'))
                  ?.long_name || '';
              const country =
                addressComponents.find((c) => c.types.includes('country'))
                  ?.short_name || '';

              setValue('city', city);
              setValue('country', country);

              // Update search query with formatted address
              setSearchQuery(results[0].formatted_address);
            }
          }
        );
      }
    }
  };

  // Search for locations using Google Places API
  useEffect(() => {
    const searchLocation = async () => {
      if (!searchQuery) {
        setSearchResults([]);
        return;
      }

      // Check if Google Maps API is loaded
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.log('Google Maps API not loaded yet');
        return;
      }

      try {
        const service = new window.google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          { input: searchQuery },
          (predictions, status) => {
            if (status === 'OK') {
              setSearchResults(predictions);
            } else {
              setSearchResults([]);
            }
          }
        );
      } catch (error) {
        console.error('Error in Google Maps autocomplete:', error);
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(searchLocation, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Handle location selection from search results
  const handleLocationSelect = (prediction) => {
    // Check if Google Maps API is loaded
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.log('Google Maps API not loaded yet');
      return;
    }

    try {
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
      service.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['geometry', 'address_components'],
        },
        (place, status) => {
          if (
            status === 'OK' &&
            place &&
            place.geometry &&
            place.geometry.location
          ) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            // Set selected location and update map
            setSelectedLocation({ lat, lng });
            setViewport({
              center: { lat, lng },
              zoom: 14,
            });
            setIsMapVisible(true);

            // Update form values
            setValue('latitude', lat.toString());
            setValue('longitude', lng.toString());

            // Update address components
            if (place.address_components) {
              const city =
                place.address_components.find((c) =>
                  c.types.includes('administrative_area_level_1')
                )?.long_name || '';
              const country =
                place.address_components.find((c) =>
                  c.types.includes('country')
                )?.short_name || '';

              setValue('city', city);
              setValue('country', country);
            }

            // Update search query with the selected prediction description
            setSearchQuery(prediction.description);
            // Clear search results after selection
            setSearchResults([]);
          }
        }
      );
    } catch (error) {
      console.error('Error getting place details:', error);
    }
  };

  // Function to geocode an address
  const geocodeAddress = (address) => {
    if (!address || !window.google || !window.google.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        // Set the selected location for the map marker
        setSelectedLocation({ lat, lng });

        // Update the viewport to center on this location
        setViewport({
          center: { lat, lng },
          zoom: 14,
        });

        // Make the map visible
        setIsMapVisible(true);

        // Update the address data with coordinates and components
        const addressComponents = results[0].address_components;

        // Update form values
        setValue('latitude', lat.toString());
        setValue('longitude', lng.toString());

        const city =
          addressComponents.find((c) => c.types.includes('administrative_area_level_1'))
            ?.long_name || '';
        const country =
          addressComponents.find((c) => c.types.includes('country'))
            ?.short_name || '';

        setValue('city', city);
        setValue('country', country);
      } else {
        alert('Adres bulunamadı. Lütfen başka bir adres deneyin.');
      }
    });
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);

      if (customs) {
        const { error } = await supabase
          .from('customs')
          .update(data)
          .eq('id', customs.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('customs').insert([data]);
        if (error) throw error;
      }

      onSuccess();
      onClose();
      reset();
    } catch (error) {
      console.error('Error saving customs:', error);
      setError('Gümrük kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto my-10">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 flex-shrink-0">
          <h2 className="text-xl font-medium text-gray-800">
            {customs ? 'Gümrük Düzenle' : 'Yeni Gümrük Ekle'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-4 overflow-y-auto">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gümrük Kodu
                </label>
                <input
                  type="text"
                  {...register('customs_code', {
                    required: 'Gümrük kodu gereklidir',
                  })}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.customs_code && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.customs_code.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gümrük Adı
                </label>
                <input
                  type="text"
                  {...register('customs_name', {
                    required: 'Gümrük adı gereklidir',
                  })}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.customs_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.customs_name.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şehir
                </label>
                <input
                  type="text"
                  {...register('city', { required: 'Şehir gereklidir' })}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.city && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.city.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ülke
                </label>
                <input
                  type="text"
                  {...register('country', { required: 'Ülke gereklidir' })}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.country && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.country.message}
                  </p>
                )}
              </div>

              {/* Search Location Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adres Ara
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Adres aramak için yazın..."
                />

                {/* Search Results */}
                {searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {searchResults.map((result) => (
                      <div
                        key={result.place_id}
                        onClick={() => handleLocationSelect(result)}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                      >
                        <div className="text-sm">{result.description}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Find Address Button */}
              <div>
                <button
                  type="button"
                  onClick={() => {
                    if (searchQuery) {
                      geocodeAddress(searchQuery);
                    } else {
                      alert('Lütfen bir adres girin.');
                    }
                  }}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Adres Konumunu Bul
                </button>
              </div>

              {/* Coordinates Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enlem (Latitude)
                  </label>
                  <input
                    type="text"
                    {...register('latitude')}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Boylam (Longitude)
                  </label>
                  <input
                    type="text"
                    {...register('longitude')}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Map Toggle Button */}
              <div>
                <button
                  type="button"
                  onClick={toggleMap}
                  className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  {isMapVisible ? 'Haritayı Gizle' : 'Haritada Göster'}
                </button>
              </div>

              {/* Find Location Button */}
              <div>
                <button
                  type="button"
                  onClick={findLocationFromCoordinates}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  Koordinatlardan Konum Bul
                </button>
              </div>

              {/* Map Component */}
              {isMapVisible && (
                <div className="relative h-[300px] mt-2">
                  <APIProvider
                    apiKey={'AIzaSyDB8KdSyduBU2vadQbJhojnwu5gNsj2f-o'}
                    libraries={['places']}
                  >
                    <Map
                      zoom={viewport.zoom}
                      center={viewport.center}
                      gestureHandling="greedy"
                      disableDefaultUI={false}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '0.375rem',
                      }}
                      onCameraChanged={(ev) => {
                        setViewport({
                          center: ev.detail.center,
                          zoom: ev.detail.zoom,
                        });
                      }}
                      onClick={handleMapClick}
                    >
                      {selectedLocation && (
                        <Marker
                          position={{
                            lat: selectedLocation.lat,
                            lng: selectedLocation.lng,
                          }}
                        />
                      )}
                    </Map>
                  </APIProvider>
                </div>
              )}
            </div>

            <div className="mt-6">
              <button
                onClick={onClose}
                className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 mb-2"
              >
                İptal
              </button>
              <button
                type="submit"
                className="w-full bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'İşleniyor...' : customs ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CustomsModal;